import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Project } from 'alpha-config-interfaces';
import { BehaviorSubject, Observable, Subject, catchError, lastValueFrom, map, of, throwError } from 'rxjs';
import { AlphaHttpClient } from './alpha-http-client';
import { CacheService } from './caching.service';
import { TenantService } from './tenant.service';

export interface SideNav {
    configs?: ISidenavConfig[];
}

export interface AlphaProject extends Project {
    appConfig: {
        navigationGuardToggle: boolean;
        userGroups: UserGroup[];
    };
    devMode: boolean;
    tenant: {
        name: string;
        id: number;
    };
}

export interface PluginMetadata {
    packageId: string;
    versionId: string;
    packageName: string;
    packageLabel: string;
    version: string;
    exposedName: string;
}

interface UsedCustomCodeInterface {
    customCodePlugins: PluginMetadata[];
    projectPlugins: PluginMetadata[];
}

export interface ISidenavConfig {
    id: number;
    menuId: string;
    type: string;
    title: string;
    icon: string;
    urlType: 'string' | 'environment';
    url: string;
    pageId: string | undefined;
    navigationMode: string;
    isHidden: boolean;
    groups: Array<UserGroup>;
    order: number;
    page: IPage;
    isUserdefSidenav: boolean;
}
@Injectable({ providedIn: 'root' })
export class ProjectService {
    private httpClient = inject(AlphaHttpClient);
    private tenantService = inject(TenantService);
    private router = inject(Router);
    private location = inject(Location);

    private dataSubject = new Subject<string>();
    private savedListSubject = new BehaviorSubject<object>({});
    private savedListSubjectObserver = this.savedListSubject.asObservable();
    private currentProject!: AlphaProject;
    private angularHttpClient = inject(HttpClient);
    private cacheService = inject(CacheService);
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_CONFIG_SERVICE_URL',
        },
    };
    defaultLogoUrl = 'assets/images/NeutrinosLogo.svg';
    getProjectPromise: Map<string, Promise<Project>> = new Map();
    baseUrl = window.environments.ALPHA_UI_CONFIG_SERVICE_URL;
    isSidebarReduced: boolean = false;
    projectPlugins: UsedCustomCodeInterface = {
        customCodePlugins: [],
        projectPlugins: [],
    };

    getAllProjects(tenent: string) {
        return this.httpClient.post<{ projects: any[] }>(this.domainUrl, '/project/fetch-all', {
            tenant: tenent ? tenent : this.tenantService.fetchTenant(),
        });
    }

    getProject(projectId: string): Promise<Project> | undefined {
        if (!this.getProjectPromise.get(projectId)) {
            const request$ = this.httpClient.post<Project>(this.domainUrl, '/project/fetch', { projectId }).pipe(
                catchError((error) => {
                    if (error.status === 404) {
                        this.router.navigate(['/404'], {
                            state: { message: 'Project not found' },
                        });
                    } else if (error.status === 401) {
                        this.router.navigate(['/unauthorized'], {
                            queryParams: { redirectUrl: encodeURIComponent(window.location.href) },
                        });
                    }
                    return throwError(() => error);
                }),
            );

            this.getProjectPromise.set(projectId, lastValueFrom(request$));
        }
        return this.getProjectPromise.get(projectId);
    }

    getAdminProject(projectId: string): Promise<Project> | undefined {
        if (!this.getProjectPromise.get(projectId)) {
            const request$ = this.angularHttpClient
                .post<Project>(window.environments.ALPHA_UI_ADMIN_SERVICE_URL + '/case-config-server/project/fetch', {
                    projectId,
                })
                .pipe(
                    catchError((error) => {
                        if (error.status === 404) {
                            this.router.navigate(['/404'], {
                                state: { message: 'Project not found' },
                            });
                        } else if (error.status === 401) {
                            this.router.navigate(['/unauthorized'], {
                                queryParams: { redirectUrl: encodeURIComponent(window.location.href) },
                            });
                        }
                        return throwError(() => error);
                    }),
                );

            this.getProjectPromise.set(projectId, lastValueFrom(request$));
        }
        return this.getProjectPromise.get(projectId);
    }

    createProject(project: { name: string; description: string }) {
        return this.httpClient.post(this.domainUrl, '/project/save', {
            tenant: this.tenantService.fetchTenant(),
            ...project,
        });
    }

    removeProject(projectId: string) {
        return this.httpClient.post(this.domainUrl, '/project/remove', {
            tenant: this.tenantService.fetchTenant(),
            projectId,
        });
    }

    setCurrentProject(project: any) {
        this.currentProject = project;
    }

    fetchCurrentProject() {
        return this.currentProject;
    }

    getProjectAppConfig() {
        return this.currentProject?.appConfig || {};
    }

    togglesidebar(): boolean {
        return (this.isSidebarReduced = !this.isSidebarReduced);
    }

    changeTask(event: any) {
        this.dataSubject.next(event);
    }

    getTask() {
        return this.dataSubject.asObservable();
    }

    savedListUpdated(event: any) {
        return this.savedListSubject.next(event);
    }

    getSavedList() {
        return this.savedListSubjectObserver;
    }

    fetchLogo(): Observable<string> {
        const project = this.fetchCurrentProject();
        const getCurrentTag = project?.currentTag ? true : false;
        const version = this.cacheService.getManifestVersion('ui_theme');
        let logoUrl = `/ui/logo/project/${project?.projectId}?getCurrentTag=${getCurrentTag}`;
        if (version) {
            logoUrl += `&version=${encodeURIComponent(version)}`;
        }

        return this.httpClient.get<ImageResponse>(this.domainUrl, logoUrl).pipe(
            map((response: any) => {
                return this.constructDataUrl(response);
            }),
            catchError(() => of('assets/images/NeutrinosLogo.svg')),
        );
    }

    loader!: string;
    async fetchLoader(isAdmin: boolean = false) {
        const url = new URL(window.location.href);
        const projectId = url.searchParams.get('projectId');
        if (!projectId) {
            return;
        }
        if (this.loader) {
            return this.loader;
        }

        // Check sessionStorage cache first
        const cacheKey = `alpha:loader:${projectId}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const { version: cachedVersion, dataUrl } = JSON.parse(cached);
                const manifestVersion = this.cacheService.getManifestVersion('ui_theme');
                // Serve from cache if no manifest yet (pre-resolver) or version matches
                if (!manifestVersion || cachedVersion === manifestVersion) {
                    this.loader = dataUrl;
                    return dataUrl;
                }
            }
        } catch {
            /* Ignore storage errors */
        }

        let project;
        if (isAdmin) {
            project = await this.getAdminProject(projectId);
        } else {
            project = await this.getProject(projectId);
        }
        const getCurrentTag = project?.currentTag ? true : false;
        const version = this.cacheService.getManifestVersion('ui_theme');
        let loaderUrl = `/ui/loader/project/${projectId}?getCurrentTag=${getCurrentTag}`;
        if (version) {
            loaderUrl += `&version=${encodeURIComponent(version)}`;
        }

        return lastValueFrom(
            this.httpClient.get<ImageResponse>(this.domainUrl, loaderUrl).pipe(
                map((response: any) => {
                    const dataUrl = this.constructDataUrl(response);
                    this.loader = dataUrl;
                    try {
                        sessionStorage.setItem(cacheKey, JSON.stringify({ version: version || '', dataUrl }));
                    } catch {
                        /* Ignore storage errors */
                    }
                    return dataUrl;
                }),
                catchError(() => of('assets/images/loader-gif-image.gif')),
            ),
        );
    }

    async fetchProjectSidenavConfigs() {
        const { projectId } = this.currentProject;
        if (!projectId) {
            return;
        }
        const sideNavFetchURL = '/page-sidenav/fetch';
        return lastValueFrom(
            this.httpClient.post<SideNav>(this.domainUrl, sideNavFetchURL, {
                projectId,
                config: { getCurrentTag: true },
            }),
        );
    }

    constructDataUrl(response: ImageResponse): string {
        return `data:${response.mimeType};base64,${response.image}`;
    }

    async getProjectFromUrl(): Promise<boolean | void> {
        const url = new URL(window.location.href);
        const projectId = url.searchParams.get('projectId') || window.alpha['project']['projectId'];
        if (!projectId) {
            return false;
        }
        if (projectId) {
            const listener = () => {
                window.alpha['project']['projectId'] = projectId;
                window.removeEventListener('alpha_ready', listener);
            };
            window.addEventListener('alpha_ready', listener);
        }

        try {
            const project = await this.getProject(projectId);
            this.setCurrentProject(project);
            return true;
        } catch (error) {
            console.log(`Failed to load project using projectId ${projectId}`);
            alert(`Failed to load project using projectId ${projectId}`);
            window.location.reload();
        }
    }

    async fetchProjectPluginDependency(project: any, tenantId: string) {
        const cacheKey = `alpha:code-deps:${project.projectId}:${tenantId}`;
        let currentVersion: string | null = null;

        try {
            const { version } = await lastValueFrom(
                this.httpClient.post<{ version: string | null }>(this.domainUrl, '/pages/fetch/code-deps/version', {
                    projectId: project.projectId,
                    tenantId,
                }),
            );
            currentVersion = version;

            if (version) {
                const cached = sessionStorage.getItem(cacheKey);
                if (cached) {
                    const entry = JSON.parse(cached);
                    if (entry.version === version) {
                        this.projectPlugins = entry.data;
                        return entry.data;
                    }
                }
            }
        } catch {
            // Version check failed, fall through to full fetch
        }

        const path = '/pages/fetch/code-deps';
        const result: UsedCustomCodeInterface = await lastValueFrom(
            this.httpClient.post(this.domainUrl, path, {
                projectId: project.projectId,
                tenantId,
            }),
        );
        this.projectPlugins = result;

        if (currentVersion) {
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify({ version: currentVersion, data: result }));
            } catch {
                // Ignore cache-write failures
            }
        }

        return result;
    }
}

interface ImageResponse {
    image: string;
    mimeType: string;
}

export interface IPage {
    pageId: string;
    designId: string;
    name: string;
    displayName: string;
    version?: string;
    order?: number;
    taskDefinitionId?: string;
    type: 'page' | 'dialog' | 'global';
    renderer?: any;
    translationMap: any;
    display: boolean;
    dependencies: dependencies[];
}

interface dependencies {
    modelPath: string;
    mappingType: string;
    comparison: string;
    value: string;
    action: string;
}

export interface UserGroup {
    id: number;
    group_name: string;
    client_id: string;
    inactive?: boolean;
    number_of_users?: number;
}
